import React from 'react';
import { Clock } from 'lucide-react';
import { PublishedVersionSummary } from '../../types';

interface VersionHistoryPanelProps {
  projectId: number;
  versions: PublishedVersionSummary[];
  onVersionClick: (checkpointId: number) => void;
}

const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  projectId,
  versions,
  onVersionClick,
}) => {
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return '';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Version History
      </h3>

      {versions.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No versions published yet.
        </p>
      ) : (
        <ul role="list" className="space-y-3">
          {versions.map((version, index) => (
            <li
              key={version.checkpoint_id}
              data-testid="version-item"
              role="button"
              tabIndex={0}
              onClick={() => onVersionClick(version.checkpoint_id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onVersionClick(version.checkpoint_id);
                }
              }}
              className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 border border-gray-100 dark:border-gray-700 rounded-lg p-3 transition-colors"
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="text-sm font-semibold text-gray-900 dark:text-white"
                  aria-label={version.version_name}
                >
                  {version.version_name}
                </span>
                {index === 0 && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                    Latest
                  </span>
                )}
              </div>

              {version.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                  {version.description}
                </p>
              )}

              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                {version.published_at && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" aria-hidden="true" />
                    {formatDate(version.published_at)}
                  </span>
                )}

                {version.entity_counts && (
                  <div className="flex items-center gap-2">
                    <span aria-label={`${version.entity_counts.models || 0} models`}>
                      {version.entity_counts.models || 0} models
                    </span>
                    <span aria-label={`${version.entity_counts.agents || 0} agents`}>
                      {version.entity_counts.agents || 0} agents
                    </span>
                    <span aria-label={`${version.entity_counts.orchestrations || 0} orchestrations`}>
                      {version.entity_counts.orchestrations || 0} orchestrations
                    </span>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default VersionHistoryPanel;
